/**
 * «Премия»: что и за что начислено за период. Спека
 * `docs/specs/2026-10-07-bonus-design.md`; числа — те же, что у
 * `prokat-runner bonus-report`, из одного модуля на сервере.
 *
 * Два вида: сотрудник (по умолчанию — вошедший, «мои премии») — категории и
 * начисления с правилом и проводками; отдел (`?view=dept`) — матрица
 * «сотрудник × категория», категория раскрывается в свои начисления. Чужие
 * премии и отдел — по праву `bonus_all`. Третий вид — «Запись бронь»
 * (`?view=zapis`, `BonusZapis.tsx`): кто и каким способом записал клиента.
 */
import { useGetIdentity } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, type ReactNode, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { Combo } from "@/components/Combo";
import { DateRangePicker } from "@/components/DateRangePicker";
import { LeadLink } from "@/components/LeadLink";
import { BONUS_ALL, has } from "@/lib/access";
import {
  type BonusCategory,
  type BonusPerson,
  type DepartmentBonus,
  KC,
  loadDepartmentBonus,
  loadUserBonus,
  loadZapisBonus,
  money,
  type UserBonus,
  окнаПремии,
} from "@/lib/bonus";
import { iso } from "@/lib/dates";
import type { CurrentUser } from "@/lib/providers";
import { ZapisReport } from "@/pages/BonusZapis";

type View = "me" | "dept" | "zapis";
type Params = { view: View; user: number | null; from: string; to: string; location: number };

const dm = (day: string) => `${day.slice(8, 10)}.${day.slice(5, 7)}`;
const hm = (at: string) => `${dm(at)} ${at.slice(11, 16)}`;

/** Вид, сотрудник, период и подразделение из адреса: ссылку можно переслать. */
function useBonusParams() {
  const [params, setParams] = useSearchParams();
  const today = iso(new Date());
  const p: Params = {
    view: params.get("view") === "dept" ? "dept" : params.get("view") === "zapis" ? "zapis" : "me",
    user: Number(params.get("user")) || null,
    from: params.get("from") || today,
    to: params.get("to") || params.get("from") || today,
    location: Number(params.get("location")) || KC,
  };
  const set = (next: Partial<Params>) => {
    const v = { ...p, ...next };
    const out: Record<string, string> = { from: v.from, to: v.to };
    if (v.view !== "me") out["view"] = v.view;
    if (v.user) out["user"] = String(v.user);
    if (v.location !== KC) out["location"] = String(v.location);
    setParams(out, { replace: true });
  };
  return [p, set] as const;
}

/** Сумма: минус — красным, ноль — бледным. */
function Amount({ v, strong = false }: { v: number; strong?: boolean }) {
  const tone = v < 0 ? "text-err" : v === 0 ? "text-fg-faint" : "";
  return <span className={`tabular-nums ${strong ? "font-semibold" : ""} ${tone}`}>{money(v)}</span>;
}

function Tile({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div
      className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4"
      data-annot={`плитка «${label}»`}
    >
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className="text-[26px] font-semibold leading-none tracking-[-0.02em] tabular-nums">{value}</div>
      {hint && <div className="text-[12px] text-fg-faint">{hint}</div>}
    </div>
  );
}

function Tiles({ r, last }: { r: { total: number; accrued: number; written_off: number }; last: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Tile label="Итого" value={<>{money(r.total)} ₽</>} hint="начислено минус списано" />
      <Tile label="Начислено" value={<>{money(r.accrued)} ₽</>} />
      <Tile
        label="Списано"
        value={<span className={r.written_off < 0 ? "text-err" : ""}>{money(r.written_off)} ₽</span>}
        hint="откаты и штрафы"
      />
      {last}
    </div>
  );
}

/** Проводки одного начисления: когда, сколько, по какой сделке. */
function Rows({ m, limit }: { m: UserBonus["categories"][number]["modules"][number]; limit: number }) {
  return (
    <div className="border-t border-border bg-bg px-4 py-2" data-annot={`проводки «${m.name}»`}>
      <table className="text-[12px]">
        <tbody>
          {m.rows.map((x) => (
            <tr key={x.id}>
              <td className="whitespace-nowrap py-0.5 pr-4 tabular-nums text-fg-muted">{hm(x.at)}</td>
              <td className="whitespace-nowrap py-0.5 pr-4 text-right">
                <Amount v={x.amount} />
              </td>
              <td className="whitespace-nowrap py-0.5 pr-4">
                {x.deal_id ? (
                  <Link to={`/deals/${x.deal_id}?tab=bonus`} className="font-mono underline" title="карточка сделки, вкладка «Премия»">
                    сделка {x.deal_id}
                  </Link>
                ) : x.lead_id ? (
                  <LeadLink id={x.lead_id} />
                ) : null}
              </td>
              <td className="py-0.5 text-fg-muted">
                {x.correction && <span className="mr-1.5 rounded bg-err-bg px-1 text-err">откат</span>}
                {x.comment}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {m.rows.length < m.count && (
        <div className="pt-1 text-[11.5px] text-fg-faint">
          Показаны последние {limit} из {m.count}.
        </div>
      )}
    </div>
  );
}

function UserReport({ r, setDay }: { r: UserBonus; setDay: (day: string) => void }) {
  const [open, setOpen] = useState<Set<number>>(new Set());
  const toggle = (id: number) =>
    setOpen((was) => {
      const next = new Set(was);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  // Доля в начисленном: полоска показывает, из чего складывается премия.
  const share = (v: number) => (r.accrued > 0 ? Math.max(0, Math.min(100, (v / r.accrued) * 100)) : 0);
  return (
    <>
      <Tiles r={r} last={<Tile label="Начислений" value={r.count} hint={r.from === r.to ? dm(r.from) : `${dm(r.from)} — ${dm(r.to)}`} />} />
      {r.days.length > 1 && (
        <div className="flex flex-wrap gap-1.5 text-[12px]" data-annot="премия по дням">
          {r.days.map((d) => (
            <button
              key={d.date}
              type="button"
              title="Показать только этот день"
              onClick={() => setDay(d.date)}
              className="rounded-[var(--radius-ui)] border border-border px-2 py-1 hover:bg-bg-hover"
            >
              <span className="text-fg-faint">{dm(d.date)}</span> <Amount v={d.total} strong />
            </button>
          ))}
        </div>
      )}
      <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="за что начислено">
        {r.categories.length === 0 ? (
          <div className="p-4 text-[12.5px] text-fg-muted">За этот период начислений нет.</div>
        ) : (
          r.categories.map((c) => (
            <div key={c.id} className="border-b border-border last:border-b-0" data-annot={`категория «${c.name}»`}>
              <div className="flex items-baseline gap-3 px-4 pb-1 pt-3">
                <h2 className="text-[13px] font-semibold">{c.name}</h2>
                <span className="text-[12px] text-fg-faint">{c.count} шт</span>
                <span className="ml-auto text-[15px]">
                  <Amount v={c.total} strong /> ₽
                </span>
              </div>
              {c.modules.map((m) => (
                <Fragment key={m.id}>
                  <button
                    type="button"
                    onClick={() => toggle(m.id)}
                    title="Показать проводки"
                    className="flex w-full items-start gap-2 px-4 py-2 text-left text-[12.5px] hover:bg-bg-hover"
                    data-annot={`начисление «${m.name}»`}
                  >
                    <span className="mt-0.5 flex-none text-fg-faint">
                      {open.has(m.id) ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="font-medium">{m.name}</span>
                      {m.manual && <span className="ml-2 text-[11.5px] text-fg-faint">вручную</span>}
                      {m.rule && <span className="block text-[12px] text-fg-muted">{m.rule}</span>}
                      <span className="mt-1 block h-1 max-w-[320px] rounded bg-neutral-bg">
                        <span className="block h-1 rounded bg-accent" style={{ width: `${share(m.accrued)}%` }} />
                      </span>
                    </span>
                    <span className="w-14 flex-none whitespace-nowrap text-right tabular-nums text-fg-muted">{m.count} шт</span>
                    <span className="w-24 flex-none whitespace-nowrap text-right">
                      <Amount v={m.total} strong />
                      {m.written_off < 0 && (
                        <span className="block text-[11.5px] text-fg-faint" title="начислено и списано откатами">
                          {money(m.accrued)} <span className="text-err">{money(m.written_off)}</span>
                        </span>
                      )}
                    </span>
                  </button>
                  {open.has(m.id) && <Rows m={m} limit={r.rows_limit} />}
                </Fragment>
              ))}
            </div>
          ))
        )}
      </section>
    </>
  );
}

/** Сумма сотрудника по категории — из её начислений. */
function categorySum(p: BonusPerson, c: BonusCategory): number {
  return c.modules.reduce((s, m) => s + (p.cells[String(m.id)]?.total ?? 0), 0);
}

/** Ячейка матрицы: чем больше доля от максимума колонки, тем плотнее фон. */
function Cell({ v, max, count, sub = false }: { v: number; max: number; count?: number | undefined; sub?: boolean }) {
  // Левая граница — у суммы категории: по ней видно, где кончается одна и начинается другая.
  const heat = v > 0 && max > 0 ? Math.round((v / max) * 26) : 0;
  return (
    <td
      className={`whitespace-nowrap px-2.5 py-1.5 text-right ${sub ? "text-[12px]" : "border-l border-border"}`}
      style={heat ? { background: `color-mix(in srgb, var(--accent) ${heat}%, transparent)` } : undefined}
      title={count ? `${count} шт` : undefined}
    >
      {v === 0 ? <span className="text-fg-faint">·</span> : <Amount v={v} />}
    </td>
  );
}

function DepartmentReport({ r, openUser }: { r: DepartmentBonus; openUser: (id: number) => void }) {
  const [open, setOpen] = useState<Set<number>>(new Set());
  const all = open.size === r.categories.length && r.categories.length > 0;
  const toggle = (id: number) =>
    setOpen((was) => {
      const next = new Set(was);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const maxOf = (get: (p: BonusPerson) => number) => Math.max(0, ...r.people.map(get));
  const head = "whitespace-nowrap px-2.5 py-2 text-right font-medium";
  return (
    <>
      <Tiles r={r} last={<Tile label="Сотрудников" value={r.people.length} hint={`с начислениями · ${r.count} шт`} />} />
      <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="премии отдела">
        {r.people.length === 0 ? (
          <div className="p-4 text-[12.5px] text-fg-muted">За этот период начислений в «{r.location.name}» нет.</div>
        ) : (
          <>
            <div className="flex items-center gap-3 px-4 pt-3 text-[12px] text-fg-muted">
              <span>Категория раскрывается в свои начисления — нажмите на заголовок. Строка — премии сотрудника.</span>
              <button
                type="button"
                className="ml-auto h-7 flex-none rounded-[var(--radius-ui)] border border-border px-2.5 hover:bg-bg-hover"
                onClick={() => setOpen(all ? new Set() : new Set(r.categories.map((c) => c.id)))}
                data-annot="развернуть категории"
              >
                {all ? "свернуть всё" : "развернуть всё"}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="text-[11.5px] text-fg-faint">
                  <tr>
                    <th rowSpan={2} className="sticky left-0 bg-bg-subtle px-4 py-2 text-left align-bottom font-medium">
                      Сотрудник
                    </th>
                    <th rowSpan={2} className={`${head} align-bottom`}>
                      Итого
                    </th>
                    {r.categories.map((c) => (
                      <th
                        key={c.id}
                        colSpan={open.has(c.id) ? c.modules.length + 1 : 1}
                        rowSpan={open.has(c.id) ? 1 : 2}
                        className={`border-l border-border px-1 py-1 align-bottom font-medium ${open.has(c.id) ? "text-left" : "text-right"}`}
                        data-annot={`колонка «${c.name}»`}
                      >
                        <button
                          type="button"
                          onClick={() => toggle(c.id)}
                          title={c.modules.map((m) => `${m.name}: ${money(m.total)}`).join("\n")}
                          className="inline-flex items-center gap-1 whitespace-nowrap rounded px-1.5 py-1 text-fg-muted hover:bg-bg-hover hover:text-fg"
                        >
                          {open.has(c.id) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                          {c.name}
                          {!open.has(c.id) && c.modules.length > 1 && <span className="text-fg-faint">{c.modules.length}</span>}
                        </button>
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {r.categories
                      .filter((c) => open.has(c.id))
                      .map((c) => (
                        <Fragment key={c.id}>
                          <th className={`${head} border-l border-border text-fg-muted`}>всего</th>
                          {c.modules.map((m) => (
                            <th key={m.id} className={`${head} cursor-help`} title={m.rule ?? (m.manual ? "вручную" : undefined)}>
                              {m.name}
                            </th>
                          ))}
                        </Fragment>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {r.people.map((p) => (
                    <tr
                      key={p.user_id}
                      className="cursor-pointer border-t border-border hover:bg-bg-hover"
                      title="Открыть премии сотрудника"
                      onClick={() => openUser(p.user_id)}
                      data-annot={`сотрудник ${p.user_id}`}
                    >
                      <td className={`sticky left-0 whitespace-nowrap bg-bg-subtle px-4 py-1.5 ${p.active ? "" : "text-fg-faint"}`}>
                        {p.name}
                        {!p.active && <span className="ml-1.5 text-[11.5px]">уволен</span>}
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 text-right" title={`${p.count} шт`}>
                        <Amount v={p.total} strong />
                      </td>
                      {r.categories.map((c) => (
                        <Fragment key={c.id}>
                          <Cell v={categorySum(p, c)} max={maxOf((x) => categorySum(x, c))} />
                          {open.has(c.id) &&
                            c.modules.map((m) => (
                              <Cell
                                key={m.id}
                                sub
                                v={p.cells[String(m.id)]?.total ?? 0}
                                count={p.cells[String(m.id)]?.count}
                                max={maxOf((x) => x.cells[String(m.id)]?.total ?? 0)}
                              />
                            ))}
                        </Fragment>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t border-border-strong font-medium">
                  <tr data-annot="итог отдела">
                    <td className="sticky left-0 bg-bg-subtle px-4 py-2">Итого</td>
                    <td className="whitespace-nowrap px-2.5 py-2 text-right">
                      <Amount v={r.total} strong />
                    </td>
                    {r.categories.map((c) => (
                      <Fragment key={c.id}>
                        <td className="whitespace-nowrap border-l border-border px-2.5 py-2 text-right">
                          <Amount v={c.total} />
                        </td>
                        {open.has(c.id) &&
                          c.modules.map((m) => (
                            <td key={m.id} className="whitespace-nowrap px-2.5 py-2 text-right text-[12px]" title={`${m.count} шт`}>
                              <Amount v={m.total} />
                            </td>
                          ))}
                      </Fragment>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </section>
      {r.categories.length > 0 && (
        <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4" data-annot="за что начисляют">
          <h2 className="text-[13px] font-semibold">За что начисляют</h2>
          <table className="mt-2 w-full text-[12.5px]">
            <tbody>
              {r.categories.flatMap((c) =>
                c.modules.map((m, i) => (
                  <tr key={m.id} className="border-t border-border align-top">
                    <td className="whitespace-nowrap py-1.5 pr-4 text-fg-muted">{i === 0 ? c.name : ""}</td>
                    <td className="whitespace-nowrap py-1.5 pr-4 font-medium">{m.name}</td>
                    <td className="py-1.5 pr-4 text-fg-muted">{m.rule ?? (m.manual ? "начисляют вручную" : "—")}</td>
                    <td className="whitespace-nowrap py-1.5 pr-4 text-right tabular-nums text-fg-muted">{m.count} шт</td>
                    <td className="whitespace-nowrap py-1.5 text-right">
                      <Amount v={m.total} strong />
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

const VIEWS: { id: View; label: string; hint: string; all?: boolean }[] = [
  { id: "me", label: "Сотрудник", hint: "мои или выбранного" },
  { id: "dept", label: "Отдел", hint: "сотрудник × категория", all: true },
  { id: "zapis", label: "Запись бронь", hint: "кто и как записал" },
];

export function BonusPage() {
  const [p, set] = useBonusParams();
  const { data: me } = useGetIdentity<CurrentUser>();
  const canAll = has(me, BONUS_ALL);
  const dept = p.view === "dept" && canAll;
  const user = useQuery({
    queryKey: ["bonus-user", p.from, p.to, p.user, p.location],
    queryFn: () => loadUserBonus({ from: p.from, to: p.to, user: p.user, location: p.location }),
    enabled: !dept && p.view !== "zapis",
  });
  const department = useQuery({
    queryKey: ["bonus-dept", p.from, p.to, p.location],
    queryFn: () => loadDepartmentBonus({ from: p.from, to: p.to, location: p.location }),
    enabled: dept,
  });
  const byZapis = p.view === "zapis";
  const zapis = useQuery({
    queryKey: ["bonus-zapis", p.from, p.to, p.user],
    queryFn: () => loadZapisBonus({ from: p.from, to: p.to, user: p.user }),
    enabled: byZapis,
  });
  const q = byZapis ? zapis : dept ? department : user;
  const locations = (dept ? department.data?.locations : user.data?.locations) ?? [];
  const mine = !p.user || p.user === me?.id;
  // Вошедший — первой строкой: он может не числиться в выбранном подразделении.
  const staff = [
    { value: "", label: `Я — ${me?.name ?? ""}` },
    ...(user.data?.staff ?? []).filter((s) => s.id !== me?.id).map((s) => ({ value: String(s.id), label: s.name })),
  ];
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2" data-annot="шапка премии">
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">
          {canAll ? "Премия" : "Мои премии"}
          {!dept && !byZapis && !mine && user.data && <span className="ml-2 font-normal text-fg-muted">{user.data.user.name}</span>}
        </h1>
        {(
          <div role="tablist" aria-label="Вид премии" className="flex gap-2">
            {VIEWS.filter((t) => canAll || !t.all).map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={p.view === t.id}
                onClick={() => set({ view: t.id, user: null })}
                className={`flex items-baseline gap-2 rounded-[var(--radius-card)] border px-4 py-2 ${
                  p.view === t.id
                    ? "border-accent bg-accent-dim text-accent-text"
                    : "border-border text-fg-muted hover:bg-bg-hover hover:text-fg"
                }`}
                data-annot={`вид «${t.label}»`}
              >
                <span className="text-[15px] font-semibold">{t.id === "me" && !canAll ? "Мои" : t.label}</span>
                <span className="text-[12px] opacity-80">{t.hint}</span>
              </button>
            ))}
          </div>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-3 text-[12.5px] text-fg-muted" data-annot="отбор премии">
          {canAll && !byZapis && locations.length > 0 && (
            <Combo
              label="Отдел"
              value={String(p.location)}
              options={locations.map((l) => ({ value: String(l.id), label: l.name }))}
              onChange={(v) => set({ location: Number(v) || KC, user: null })}
              width={200}
            />
          )}
          {canAll && byZapis && (
            <Combo
              label="Сотрудник"
              value={p.user ? String(p.user) : ""}
              options={[{ value: "", label: "Все" }, ...(zapis.data?.staff ?? []).map((s) => ({ value: String(s.id), label: s.name }))]}
              onChange={(v) => set({ user: Number(v) || null })}
              placeholder="имя сотрудника"
              width={240}
            />
          )}
          {canAll && !dept && !byZapis && (
            <Combo
              label="Сотрудник"
              value={mine ? "" : String(p.user)}
              options={staff}
              onChange={(v) => set({ user: Number(v) || null })}
              placeholder="имя сотрудника"
              width={240}
            />
          )}
          <DateRangePicker label="Период" from={p.from} to={p.to} windows={окнаПремии()} onChange={(from, to) => set({ from, to: to || from })} />
        </div>
      </div>
      {q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Читаю биллинг CRM…</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить премии: {String(q.error.message)}</div>
      ) : byZapis ? (
        zapis.data && <ZapisReport r={zapis.data} pick={canAll ? (id) => set({ user: id }) : undefined} />
      ) : dept ? (
        department.data && <DepartmentReport r={department.data} openUser={(id) => set({ view: "me", user: id })} />
      ) : (
        user.data && <UserReport r={user.data} setDay={(day) => set({ from: day, to: day })} />
      )}
    </main>
  );
}
